import { Holding, Cliente, Propiedad, Caso, Usuario, AnalisisEstudio, Reclamo, Cobro, Tesoreria } from "../../types";
import { Badge, getEstadoCasoBadgeVariant } from "../common/Badge";

interface DashboardProps {
  holdings: Holding[];
  clientes: Cliente[];
  propiedades: Propiedad[];
  casos: Caso[];
  usuarios: Usuario[];
  analisisEstudios: AnalisisEstudio[];
  reclamos: Reclamo[];
  cobros: Cobro[];
  tesoreriaItems: Tesoreria[];
}

export function Dashboard({
  holdings,
  clientes,
  propiedades,
  casos,
  usuarios,
  analisisEstudios,
  reclamos,
  cobros,
  tesoreriaItems,
}: DashboardProps) {
  // Holdings stats
  const totalHoldings = holdings.length;
  const holdingsActivos = holdings.filter((h) => h.activo).length;

  // Clients stats
  const totalClientes = clientes.length;
  const clientesSinHolding = clientes.filter((c) => !c.holdingId).length;
  const clientesConHolding = clientes.filter((c) => c.holdingId).length;

  // Properties stats
  const totalPropiedades = propiedades.length;
  const propActivas = propiedades.filter((p) => p.estadoPropiedad === "activa").length;
  const propDesvinculadas = propiedades.filter((p) => p.estadoPropiedad === "desvinculada").length;
  const propBloqueadas = propiedades.filter((p) => p.estadoPropiedad === "bloqueada").length;

  // Analysis stats
  const totalAnalisis = analisisEstudios.length;
  const analisisEnEstudio = analisisEstudios.filter((a) => a.estado === "en_estudio").length;
  const analisisCompletados = analisisEstudios.filter((a) => a.estado === "completado").length;
  const analisisDesistidos = analisisEstudios.filter((a) => a.estado === "desistido").length;

  // Cases stats
  const totalCasos = casos.length;
  const casosActivos = casos.filter(
    (c) => !["cobro_facturacion", "desistido"].includes(c.estado)
  ).length;
  const casosDesistidos = casos.filter((c) => c.estado === "desistido").length;
  const casosGanados = casos.filter((c) => c.resultado === "ganado" || c.resultado === "parcialmente_ganado").length;
  const casosPerdidos = casos.filter((c) => c.resultado === "perdido").length;

  // Post-solicitud stats
  const reclamosActivos = reclamos.filter((r) => !["resuelto", "desistido"].includes(r.estado)).length;
  const cobrosPendientes = cobros.filter((c) => c.estado !== "cobrado" && c.estado !== "incobrable").length;
  const tesoreriaEsperando = tesoreriaItems.filter((t) => t.estado === "esperando_pago").length;

  // Cases by estado
  const casosPorEstado = {
    rol_listo: casos.filter((c) => c.estado === "rol_listo").length,
    en_preparacion: casos.filter((c) => c.estado === "en_preparacion").length,
    confirmacion_presentacion: casos.filter((c) => c.estado === "confirmacion_presentacion").length,
    solicitud_administrativa: casos.filter((c) => c.estado === "solicitud_administrativa").length,
    resolucion: casos.filter((c) => c.estado === "resolucion").length,
    ganado: casos.filter((c) => c.estado === "ganado").length,
    parcialmente_ganado: casos.filter((c) => c.estado === "parcialmente_ganado").length,
    perdido: casos.filter((c) => c.estado === "perdido").length,
    cobro_facturacion: casos.filter((c) => c.estado === "cobro_facturacion").length,
    tesoreria: casos.filter((c) => c.estado === "tesoreria").length,
    reclamo: casos.filter((c) => c.estado === "reclamo").length,
    desistido: casos.filter((c) => c.estado === "desistido").length,
  };

  // Financial stats
  const totalRebajaEsperada = casos.reduce(
    (sum, c) => sum + (c.rebajaEsperada || 0),
    0
  );
  const totalHonorarios = casos.reduce((sum, c) => sum + (c.honorario || 0), 0);
  const honorariosGanados = casos
    .filter((c) => c.resultado === "ganado")
    .reduce((sum, c) => sum + (c.honorario || 0), 0);

  // Recent cases (last 5)
  const recentCasos = [...casos]
    .sort(
      (a, b) =>
        new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime()
    )
    .slice(0, 5);

  const formatMoney = (value: number) => `$${value.toLocaleString("es-CL")}`;

  const getHoldingNombre = (holdingId: number) =>
    holdings.find((h) => h.id === holdingId)?.nombre || "-";

  return (
    <div className="p-6 h-full overflow-y-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Dashboard</h1>

      {/* Main stats grid */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Holdings</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{totalHoldings}</p>
          <p className="text-xs text-slate-500 mt-1">{holdingsActivos} activos</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Clientes</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{totalClientes}</p>
          <p className="text-xs text-slate-500 mt-1">
            {clientesConHolding} asignados, {clientesSinHolding} sin holding
          </p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Propiedades</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{totalPropiedades}</p>
          <p className="text-xs text-slate-500 mt-1">
            {propActivas} activas{propDesvinculadas > 0 && `, ${propDesvinculadas} desv.`}{propBloqueadas > 0 && `, ${propBloqueadas} bloq.`}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Analysis</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{totalAnalisis}</p>
          <p className="text-xs text-blue-600 mt-1">{analisisEnEstudio} en estudio, {analisisCompletados} completados</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Solicitudes</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{totalCasos}</p>
          <p className="text-xs text-green-600 mt-1">{casosActivos} activos</p>
        </div>
      </div>

      {/* Post-solicitud stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
          <p className="text-xs text-orange-600 uppercase tracking-wide">Reclamos</p>
          <p className="text-3xl font-bold text-orange-800 mt-1">{reclamos.length}</p>
          <p className="text-xs text-orange-600 mt-1">{reclamosActivos} activos</p>
        </div>
        <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-4">
          <p className="text-xs text-indigo-600 uppercase tracking-wide">Cobros</p>
          <p className="text-3xl font-bold text-indigo-800 mt-1">{cobros.length}</p>
          <p className="text-xs text-indigo-600 mt-1">{cobrosPendientes} pendientes</p>
        </div>
        <div className="bg-teal-50 rounded-lg border border-teal-200 p-4">
          <p className="text-xs text-teal-600 uppercase tracking-wide">Tesoreria SII</p>
          <p className="text-3xl font-bold text-teal-800 mt-1">{tesoreriaItems.length}</p>
          <p className="text-xs text-teal-600 mt-1">{tesoreriaEsperando} esperando pago</p>
        </div>
      </div>

      {/* Cases breakdown */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Cases by estado */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Solicitudes por Estado</h3>
          <div className="space-y-2">
            {Object.entries(casosPorEstado).map(([estado, count]) => (
              <div key={estado} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant={getEstadoCasoBadgeVariant(estado)}>
                    {estado.replace("_", " ")}
                  </Badge>
                </div>
                <span className="text-sm font-medium text-slate-700">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cases results */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Resumen de Resultados</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-600">Casos Ganados</p>
              <p className="text-2xl font-bold text-green-800">{casosGanados}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-red-600">Casos Perdidos</p>
              <p className="text-2xl font-bold text-red-800">{casosPerdidos}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3">
              <p className="text-xs text-yellow-600">Casos Activos</p>
              <p className="text-2xl font-bold text-yellow-800">{casosActivos}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-600">Casos Desistidos</p>
              <p className="text-2xl font-bold text-slate-800">{casosDesistidos}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <p className="text-xs text-blue-600 uppercase tracking-wide">
            Rebaja Esperada Total
          </p>
          <p className="text-2xl font-bold text-blue-800 mt-1">
            {formatMoney(totalRebajaEsperada)}
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
          <p className="text-xs text-purple-600 uppercase tracking-wide">
            Honorarios Totales
          </p>
          <p className="text-2xl font-bold text-purple-800 mt-1">
            {formatMoney(totalHonorarios)}
          </p>
        </div>

        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
          <p className="text-xs text-emerald-600 uppercase tracking-wide">
            Honorarios Ganados
          </p>
          <p className="text-2xl font-bold text-emerald-800 mt-1">
            {formatMoney(honorariosGanados)}
          </p>
        </div>
      </div>

      {/* Recent cases */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Casos Recientes</h3>
        {recentCasos.length > 0 ? (
          <div className="space-y-2">
            {recentCasos.map((caso) => (
              <div
                key={caso.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Badge variant={getEstadoCasoBadgeVariant(caso.estado)}>
                    {caso.estado.replace("_", " ")}
                  </Badge>
                  <div>
                    <p className="font-medium text-sm text-slate-800">{caso.titulo}</p>
                    <p className="text-xs text-slate-500">
                      {getHoldingNombre(caso.holdingId)} - {caso.tipoPresentacion}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">
                    {new Date(caso.fechaCreacion).toLocaleDateString("es-CL")}
                  </p>
                  {caso.honorario && (
                    <p className="text-xs text-slate-600">{formatMoney(caso.honorario)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No hay casos</p>
        )}
      </div>
    </div>
  );
}
